import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  ShowChart,
  TrendingUp,
  Devices,
  PhoneAndroid,
  Headphones,
  CalendarToday,
  Security,
  Business,
  Apps,
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import adminApi from '../services/api';
import { UniqueDeviceAnalyticsResponse } from '../types/api';

interface UniqueDeviceAnalyticsProps {
  dateRange: string;
}

const COLORS = ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', '#00f2fe', '#43e97b', '#38f9d7'];

const UniqueDeviceAnalytics: React.FC<UniqueDeviceAnalyticsProps> = ({ dateRange }) => {
  const [data, setData] = useState<UniqueDeviceAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uniqueBy, setUniqueBy] = useState<'device_pair' | 'phone_mac' | 'earbuds_mac'>('device_pair');
  const [requireHmac, setRequireHmac] = useState(true);
  const [includeComparison, setIncludeComparison] = useState(true);

  useEffect(() => {
    loadUniqueDeviceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, uniqueBy, requireHmac]);

  const loadUniqueDeviceData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const dateRangeData = adminApi.getDateRange(dateRange as 'today' | 'week' | 'month' | 'quarter');
      
      const response = await adminApi.getUniqueDeviceAnalytics({
        ...dateRangeData,
        unique_by: uniqueBy,
        group_by: dateRange === 'today' ? 'hour' : 'day',
        require_hmac_seed: requireHmac,
        include_app_breakdown: true,
        include_vendor_breakdown: true,
        comparison_period: includeComparison,
      });

      setData(response);
    } catch (err: any) {
      setError(err.message || 'Failed to load unique device analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat().format(num);
  };

  const formatPercentage = (value: number): string => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const getTrendIcon = (trend?: string) => {
    if (!trend) return null;
    switch (trend) {
      case 'increasing':
        return <TrendingUp sx={{ color: 'success.main' }} />;
      case 'decreasing':
        return <TrendingUp sx={{ color: 'error.main', transform: 'rotate(180deg)' }} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!data) {
    return <Alert severity="info">No data available</Alert>;
  }

  // Prepare chart data with cumulative counts
  const chartData = data.time_series.map((entry, index) => {
    const cumulative = data.time_series
      .slice(0, index + 1)
      .reduce((sum, e) => sum + e.count, 0);
    
    return {
      ...entry,
      period: dateRange === 'today' 
        ? new Date(entry.period).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : new Date(entry.period).toLocaleDateString([], { month: 'short', day: 'numeric' }),
      cumulative_count: cumulative,
    };
  });

  return (
    <>
      {/* Controls */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Unique By</InputLabel>
            <Select
              value={uniqueBy}
              onChange={(e: SelectChangeEvent) => setUniqueBy(e.target.value as any)}
              label="Unique By"
            >
              <MenuItem value="device_pair">Device Pair</MenuItem>
              <MenuItem value="phone_mac">Phone MAC</MenuItem>
              <MenuItem value="earbuds_mac">Earbuds MAC</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Require HMAC</InputLabel>
            <Select
              value={requireHmac ? 'yes' : 'no'}
              onChange={(e: SelectChangeEvent) => setRequireHmac(e.target.value === 'yes')}
              label="Require HMAC"
            >
              <MenuItem value="yes">Yes (Successful Only)</MenuItem>
              <MenuItem value="no">No (All Attempts)</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Comparison</InputLabel>
            <Select
              value={includeComparison ? 'yes' : 'no'}
              onChange={(e: SelectChangeEvent) => setIncludeComparison(e.target.value === 'yes')}
              label="Comparison"
            >
              <MenuItem value="yes">With Previous Period</MenuItem>
              <MenuItem value="no">Current Period Only</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" gutterBottom>Total New Devices</Typography>
                  <Typography variant="h3" fontWeight="bold">
                    {formatNumber(data.total_unique_devices)}
                  </Typography>
                  {data.comparison_data && (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                      {getTrendIcon(data.comparison_data.trend)}
                      <Typography variant="body2">
                        {data.comparison_data.change_percentage > 0 ? '+' : ''}
                        {(data.comparison_data.change_percentage || 0).toFixed(1)}%
                      </Typography>
                    </Stack>
                  )}
                </Box>
                <Devices sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" gutterBottom>New Phones</Typography>
                  <Typography variant="h3" fontWeight="bold">
                    {formatNumber(data.unique_phones)}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {formatPercentage(data.unique_phones / Math.max(data.total_unique_devices, 1))} of total
                  </Typography>
                </Box>
                <PhoneAndroid sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" gutterBottom>New Earbuds</Typography>
                  <Typography variant="h3" fontWeight="bold">
                    {formatNumber(data.unique_earbuds)}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {formatPercentage(data.unique_earbuds / Math.max(data.total_unique_devices, 1))} of total
                  </Typography>
                </Box>
                <Headphones sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" gutterBottom>Daily Average</Typography>
                  <Typography variant="h3" fontWeight="bold">
                    {(data.daily_average || 0).toFixed(1)}
                  </Typography>
                  {data.peak_day && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Peak: {new Date(data.peak_day.date).toLocaleDateString()}
                    </Typography>
                  )}
                </Box>
                <CalendarToday sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Time Series Chart */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShowChart /> New Devices Over Time
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorNew" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#667eea" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#667eea" stopOpacity={0.1}/>
                    </linearGradient>
                    <linearGradient id="colorCumulative" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#43e97b" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#43e97b" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="count"
                    name="New Devices"
                    stroke="#667eea"
                    fillOpacity={1}
                    fill="url(#colorNew)"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="cumulative_count"
                    name="Cumulative Total"
                    stroke="#43e97b"
                    fillOpacity={1}
                    fill="url(#colorCumulative)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Additional Analytics */}
      <Grid container spacing={3}>
        {/* Attestation Stats */}
        {data.attestation_stats && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Security /> Attestation Security
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="body2">With Attestation</Typography>
                    <Typography variant="body2">
                      {formatNumber(data.attestation_stats.total_with_attestation)} ({formatPercentage(data.attestation_stats.total_with_attestation / Math.max(data.total_unique_devices, 1))})
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="body2">Verified</Typography>
                    <Typography variant="body2">
                      {formatNumber(data.attestation_stats.verified_attestations)} ({formatPercentage(data.attestation_stats.verified_attestations / Math.max(data.attestation_stats.total_with_attestation, 1))})
                    </Typography>
                  </Stack>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Security Levels</Typography>
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">StrongBox</Typography>
                      <Chip size="small" label={formatNumber(data.attestation_stats.strongbox_devices)} color="success" />
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">TEE</Typography>
                      <Chip size="small" label={formatNumber(data.attestation_stats.tee_devices)} color="primary" />
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">Software</Typography>
                      <Chip size="small" label={formatNumber(data.attestation_stats.software_attestation)} color="default" />
                    </Stack>
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* App Breakdown */}
        {data.app_breakdown && data.app_breakdown.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Apps /> Top Applications (New Devices)
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Application</TableCell>
                        <TableCell align="right">Devices</TableCell>
                        <TableCell align="right">%</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.app_breakdown.slice(0, 5).map((app, index) => (
                        <TableRow key={index}>
                          <TableCell>{app.package_name}</TableCell>
                          <TableCell align="right">{formatNumber(app.device_count)}</TableCell>
                          <TableCell align="right">{(app.percentage || 0).toFixed(1)}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Vendor Device Breakdown */}
        {data.vendor_device_breakdown && data.vendor_device_breakdown.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Business /> Vendor Device Breakdown
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.vendor_device_breakdown.slice(0, 10)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="device_name" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="device_count" fill="#667eea" name="New Devices">
                      {data.vendor_device_breakdown.slice(0, 10).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default UniqueDeviceAnalytics;